import formStyles from '@edu-flow/styles/Form.module.css';
import { type ChangeEventHandler, type FC, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import useFieldEvents from '@core/hooks/cohesion/useFieldEvents';
import { selectFormCorrelationId } from '@core/reducers/eventingSlice';
import { selectInput, setInput, setInputs } from '@core/reducers/inputsSlice';
import cn from '@core/utils/classNames';
import convertToSlug from '@core/utils/strings/convertToSlug';

import type { FieldComponentProps } from '../types';
import styles from './TextField.module.css';

const TextField: FC<FieldComponentProps> = ({ field, fieldError, heading, fieldNumber }) => {
  // Action Dispatcher
  const dispatch = useDispatch();

  const [canFireFieldInputted, setCanFireFieldInputted] = useState(true);

  // Form Correlation Id for events
  const formCorrelationId = useSelector(selectFormCorrelationId);

  const { fieldInputted, fieldViewed } = useFieldEvents({
    correlationId: formCorrelationId,
    userInputField: {
      autofilled: false,
      fieldName: field.name,
      fieldLabel: heading,
      fieldType: field.type,
      fieldNumber,
    },
  });

  useEffect(() => {
    fieldViewed();
  }, []);

  // Inputs State properties
  const fieldInput = useSelector((state) => selectInput(state, field.name));

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { value } = event.target;

    if (field.name !== 'fullName') dispatch(setInput({ key: field.name as string, value }));
    else {
      const splitFirstName = value.split(' ')[0];
      const splitLastName = value.split(' ')[1];
      dispatch(
        setInputs([
          { key: 'fullName', value },
          { key: 'firstName', value: splitFirstName },
          { key: 'lastName', value: splitLastName },
        ])
      );
    }

    dispatch(setInput({ key: field.name, value }));
    if (canFireFieldInputted) {
      // Field Eventing
      fieldInputted({
        userInputField: {
          fieldValue: 'SANITIZED',
        },
      });
      setCanFireFieldInputted(false);
    }
  };

  return (
    <div className={formStyles.fieldGroup}>
      <input
        id={field.name}
        type={field.format}
        className={cn(styles.textField, fieldError && styles.error)}
        name={field.name}
        placeholder={field.label}
        min={field.min || undefined}
        max={field.max || undefined}
        value={fieldInput}
        onChange={handleChange}
        onFocus={() => setCanFireFieldInputted(true)}
        data-testid={convertToSlug(field.name as string)}
        aria-label={field.label}
        autoComplete={field.autocompleteValue}
      />
    </div>
  );
};

export default TextField;
